/**
 * Represents a walkthrough tour for guiding users through a series of steps.
 * This class is responsible for initializing and starting the tour, as well as handling
 * the confirmation for closing the tour.
 */
class Walkthrough {
	// eslint-disable-next-line require-jsdoc
	constructor() {
		// Make sure the data we need is available
		if ( _uo.utility.isEmpty( this.walkthroughID ) ) {
			throw new Error( 'Walkthrough ID is missing.' );
		}

		if ( _uo.utility.isEmpty( this.tourStartEvents ) ) {
			throw new Error( 'Tour start events are missing.' );
		}

		// Check if the walkthrough is enabled
		if ( ! UncannyAutomator._core.recipe.walkthrough.shouldShowWalkthrough( this.walkthroughID ) ) {
			return;
		}

		// Start the tour
		this.handleTourStart();
	}

	/**
	 * Creates a new tour instance and adds the predefined steps to it.
	 *
	 * The tour instance is created with a set of default options, including
	 * extra classes for the step's content element, padding and radius for the
	 * modal overlay opening, an enabled cancel icon, and smooth scrolling behavior.
	 *
	 * After the tour instance is created, the predefined steps are added to it.
	 */
	createTour() {
		// Set the tour instance
		this.tour = new _uo.utility.walkthrough.Tour( {
			id: this.walkthroughID,
			useModalOverlay: true,
			// Disable keyboard navigation
			keyboardNavigation: false,
			defaultStepOptions: {
				// A string of extra classes to add to the step’s content element.
				classes: 'uap uap-shepherd',
				// An amount of padding to add around the modal overlay opening
				modalOverlayOpeningPadding: 15,
				// The radius of the modal overlay opening
				modalOverlayOpeningRadius: 10,
				cancelIcon: {
					enabled: true
				},
				scrollTo: {
					behavior: 'smooth',
					block: 'center'
				}
			}
		} );

		// Add the steps to the tour
		this.tour.addSteps( this.steps );
	}

	/**
	 * Initializes the tour start process by attaching event listeners to the document.
	 * Each event specified in `this.tourStartEvents` will trigger the start of the tour.
	 */
	handleTourStart() {
		this.tourStartEvents.forEach( eventName => {
			document.addEventListener( eventName, event => {
				this.startTour();
			} )
		} );
	}

	/**
	 * Starts the tour by setting up necessary event handlers, creating the tour instance, and then starting the tour. It also adds global event listeners that are relevant to all steps of the tour.
	 */
	startTour() {
		// Set the event handlers
		this.eventHandlers = new Map();

		// Create the tour instance
		this.createTour();

		// Start the tour
		this.tour.start();

		// Set current progress
		this.setWalkthroughProgress();

		// Add event listeners that affect all steps
		this.addTourListeners();
	}

	/**
	 * Adds event listeners for tour events.
	 */
	addTourListeners() {
		/**
		 * Sets up an event listener for the 'cancel' event of the tour.
		 *
		 * This method sets up a handler for the 'cancel' event of the tour. When the tour is cancelled, it calls the `confirmCloseTour` method.
		 *
		 * @listens tour#cancel
		 */
		this.tour.on( 'cancel', this.confirmCloseTour.bind( this ) );

		/**
		 * Sets up an event listener for the 'complete' event of the tour.
		 *
		 * This method sets up a handler for the 'complete' event of the tour. When the tour is completed, it calls the `finishTour` method.
		 *
		 * @listens tour#complete
		 */
		this.tour.on( 'complete', this.finishTour.bind( this ) );

		/**
		 * Sets up an event listener for the 'show' event of the tour.
		 *
		 * This method sets up a handler for the 'show' event of the tour. When a step is shown, it sets up another event listener for the 'hide' event of the step. When the step is hidden, it calls the `cleanUpStep` method with the step's ID.
		 *
		 * @listens tour#show
		 * @listens step#hide
		 */
		this.tour.on( 'show', details => {
			// Set current progress
			this.setWalkthroughProgress();

			// eslint-disable-next-line require-jsdoc
			details.step.once( 'hide', () => {
				this.cleanUpStep( details.step.options.id );
			} );
		} );
	}

	/**
	 * Finishes the tour.
	 *
	 * This method removes the event listeners and closes the tour by setting the walkthrough mode to false.
	 */
	finishTour() {
		// Remove the event listeners
		this.cleanUp();

		// Close the tour
		this.setWalkthroughProgress( { closeRequested: true } );
	}

	/**
	 * Sets the current progress of the walkthrough.
	 * This method updates the walkthrough progress by sending the current step ID, progress percentage, and a flag indicating if the close was requested
	 *
	 * @param {Object} params - The parameters for setting the walkthrough progress.
	 * @param {Boolean} params.closeRequested - Indicates whether the close of the walkthrough was requested.
	 */
	setWalkthroughProgress( params = { closeRequested: false } ) {
		const { closeRequested } = params;

		// Set current progress
		UncannyAutomator._core.recipe.walkthrough.setWalkthroughProgress(
			this.walkthroughID,
			{
				closeRequested: closeRequested,
				stepID: this.tour.getCurrentStep().id,
				progressPercentage: this.progressPercentage
			}
		);
	}

	/**
	 * Confirms if the user wants to close the tour.
	 *
	 * @param {Event} event - The event that triggered the confirmation.
	 * @return {Promise<void>} - A Promise that resolves when the confirmation is handled.
	 * @throws Will restart the tour if the user does not confirm.
	 */
	async confirmCloseTour( event ) {
		// Get current step
		const currentStep = this.tour.getCurrentStep();

		try {
			await _uo.utility.confirm( {
				heading: this.strings.close_walkthrough.confirmation_title,
				content: this.strings.close_walkthrough.confirmation_message,
				confirmButtonLabel: this.strings.close_walkthrough.confirmation_button_label
			} );
		} catch ( e ) {
			// Restart the tour if the user does not confirm
			this.tour.show( currentStep.id );

			// Stop here
			return;
		}

		// Close the tour
		this.setWalkthroughProgress( { closeRequested: true } );
	}

	/**
	 * Cleans up event listeners for the current step when it is hidden.
	 *
	 * This method retrieves all event listeners associated with the specified step ID and removes them. It then deletes the reference to these listeners from the `eventHandlers` map to free up memory.
	 *
	 * @param {String} stepID - The ID of the step that is being hidden.
	 */
	cleanUpStep( stepID ) {
		// Retrieve the map of event listeners for the specified step ID
		const stepHandlers = this.eventHandlers.get( stepID );

		// If there are no event listeners for this step, exit the function
		if ( ! stepHandlers ) {
			return;
		}

		// Iterate over each removal function in the map and call it to remove the event listener
		stepHandlers.forEach( removeListeners => {
			removeListeners.forEach( removeListener => removeListener() );
		} );

		// Remove the reference to the event listeners for this step from the eventHandlers map
		this.eventHandlers.delete( stepID );
	}

	/**
	 * Cleans up all event listeners for all steps.
	 *
	 * This method iterates over all the entries in the `eventHandlers` map, calling each removal function to remove the associated event listeners.
	 *
	 * After all event listeners are removed, it clears the `eventHandlers` map to free up memory.
	 */
	cleanUp() {
		// Iterate over each entry in the eventHandlers map
		this.eventHandlers.forEach( ( removeListeners, stepID ) => {
			// For each step, iterate over all removal functions and call them to remove the event listeners
			removeListeners.forEach( eventHandlersMap => {
				eventHandlersMap.forEach( removeListener => removeListener() );
			} );
		} );

		// Clear the eventHandlers map to remove all references to the event listeners
		this.eventHandlers.clear();
	}

	/**
	 * Adds an event listener and stores the removal function.
	 *
	 * This method attaches an event listener to the document and stores the corresponding removal function in a nested map structure, organized by step ID and event type.
	 *
	 * @param {String} stepID - The ID of the step.
	 * @param {String} event - The event type.
	 * @param {Function} handler - The event handler.
	 */
	addEventListener( stepID, event, handler ) {
		// Attach the event listener to the document
		document.addEventListener( event, handler );

		// If the eventHandlers map doesn't have an entry for this step, create a new map for it
		if ( ! this.eventHandlers.has( stepID ) ) {
			this.eventHandlers.set( stepID, new Map() );
		}

		// Retrieve the map of event listeners for the specified step
		const stepHandlers = this.eventHandlers.get( stepID );

		// If the step's map doesn't have an entry for this event type, create a new array for it
		if ( ! stepHandlers.has( event ) ) {
			stepHandlers.set( event, [] );
		}

		// Add the removal function (anonymous function) to the array of removal functions for this event type
		stepHandlers.get( event ).push( () => {
			document.removeEventListener( event, handler );
		} );
	}

	/**
	 * Translatable strings for the walkthrough.
	 * 
	 * @return {Object} The translatable strings for the walkthrough
	 */
	get strings() {
		return [];
	};

	/**
	 * The ID of the walkthrough.
	 * 
	 * @return {String} The ID of the walkthrough
	 */
	get walkthroughID() {
		return '';
	};

	/**
	 * Events that start the tour
	 * 
	 * @return {Array} The events that start the tour
	 */
	get tourStartEvents() {
		return [];
	};

	/**
	 * Steps for the walkthrough
	 * 
	 * @return {Array<Object>} The steps for the walkthrough
	 */
	get steps() {
		return [];
	}

	/**
	 * Calculates the progress percentage of the current step
	 * 
	 * @returns {Number} The current progress of the tour as a percentage
	 */
	get progressPercentage() {
		// Get all steps of the tour
		const steps = this.tour.steps;

		// Get the current active step
		const currentStep = this.tour.getCurrentStep();  

		// Find the index of the current step
		const currentIndex = steps.findIndex( step => step.id === currentStep.id );

		// Calculate progress percentage
		const progress = ( currentIndex + 1 ) / steps.length * 100;

		return parseInt( progress );
	}
}

export default Walkthrough;
